import { generateContentHash } from './ai-cache-service';
import { getCachedItem, setCachedItem, cleanupExpiredItems, clearAllCaches as clearAllClientCaches, getCacheKeys } from '@/utils/clientCacheUtils';
import { MessageType, TopicType } from '@/types/assistant';
import pako from 'pako'; // For compression/decompression

// Store names
const EMBEDDING_STORE = 'embeddings';
const RESPONSE_STORE = 'responses';
const MESSAGES_STORE = 'messages';
const CACHE_STATS_STORE = 'cache_stats';
const CACHE_RELATIONSHIPS_STORE = 'cache_relationships';

// Cache TTLs in milliseconds
export const CLIENT_CACHE_TTL = {
  EMBEDDING: 7 * 24 * 60 * 60 * 1000, // 7 days
  RESPONSE: 24 * 60 * 60 * 1000,      // 24 hours
  MESSAGES: 3 * 24 * 60 * 60 * 1000,  // 3 days
  FREQUENTLY_USED: 14 * 24 * 60 * 60 * 1000, // 14 days for frequently used items
  RARELY_USED: 12 * 60 * 60 * 1000,   // 12 hours for rarely used items
};

// Simple event bus for cache invalidation events
type CacheEventHandler = (payload: any) => void;
type CacheEventType = 'knowledgeUpdated' | 'topicUpdated' | 'languageUpdated' | 'contentRelationshipChanged';

class CacheEventBus {
  private listeners: Record<string, CacheEventHandler[]> = {};
  
  subscribe(event: CacheEventType, handler: CacheEventHandler) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(handler);
    
    // Return unsubscribe function
    return () => {
      this.listeners[event] = this.listeners[event].filter(h => h !== handler);
    };
  }
  
  publish(event: CacheEventType, payload: any) {
    if (!this.listeners[event]) return;
    
    this.listeners[event].forEach(handler => {
      try {
        handler(payload);
      } catch (error) {
        console.error(`Error in cache event handler for ${event}:`, error);
      }
    });
  }
}

// Create a singleton event bus
export const cacheEventBus = new CacheEventBus();

// Export clearAllCaches from clientCacheUtils for use in other components
export const clearAllCaches = clearAllClientCaches;

// Helper function to compress embedding vectors for storage efficiency
const compressEmbedding = (embedding: number[]): Uint8Array => {
  // Convert embedding to Float32Array for consistent representation
  const float32Array = new Float32Array(embedding);
  // Convert to binary data
  const binaryData = new Uint8Array(float32Array.buffer);
  // Compress the binary data
  return pako.deflate(binaryData);
};

// Helper function to decompress embedding vectors
const decompressEmbedding = (compressedData: Uint8Array): number[] => {
  try {
    // Decompress the data
    const decompressedData = pako.inflate(compressedData);
    // Convert back to Float32Array
    const float32Array = new Float32Array(decompressedData.buffer);
    // Convert to regular array
    return Array.from(float32Array);
  } catch (error) {
    console.error('Error decompressing embedding:', error);
    return [];
  }
};

// Track content relationships for sophisticated cache invalidation
export const trackContentRelationship = async (
  sourceKey: string,
  sourceType: string,
  relatedKeys: string[],
  relatedType: string
): Promise<boolean> => {
  try {
    await setCachedItem(
      `${sourceKey}_${sourceType}_relationships`,
      { relatedKeys, relatedType, lastUpdated: new Date() },
      CACHE_RELATIONSHIPS_STORE,
      { maxAge: 30 * 24 * 60 * 60 * 1000 } // 30 days
    );
    return true;
  } catch (error) {
    console.error('Error tracking content relationship:', error);
    return false;
  }
};

// Get related content keys
export const getRelatedContentKeys = async (
  sourceKey: string,
  sourceType: string
): Promise<{relatedKeys: string[], relatedType: string} | null> => {
  try {
    return await getCachedItem(
      `${sourceKey}_${sourceType}_relationships`, 
      CACHE_RELATIONSHIPS_STORE
    );
  } catch (error) {
    console.error('Error getting related content keys:', error);
    return null;
  }
};

// Client-side Embedding Cache Functions with progressive loading support
export const getClientEmbedding = async (
  text: string, 
  model: string = 'text-embedding-3-small',
  options: { chunkSize?: number } = {}
): Promise<number[] | null> => {
  const contentHash = generateContentHash(text, { model });
  const cachedItem = await getCachedItem<{embedding: Uint8Array}>(
    contentHash, 
    EMBEDDING_STORE,
    { loadChunks: options.chunkSize !== undefined }
  );
  
  let embedding = null;
  
  // If found in cache, decompress it
  if (cachedItem && cachedItem.embedding) {
    try {
      embedding = decompressEmbedding(cachedItem.embedding);
      updateCacheMetrics(EMBEDDING_STORE, 'hit');
    } catch (error) {
      console.error('Error decompressing cached embedding:', error);
      updateCacheMetrics(EMBEDDING_STORE, 'miss');
    }
  } else {
    updateCacheMetrics(EMBEDDING_STORE, 'miss');
  }
  
  return embedding;
};

export const setClientEmbedding = async (
  text: string,
  embedding: number[],
  model: string = 'text-embedding-3-small'
): Promise<boolean> => {
  const contentHash = generateContentHash(text, { model });
  
  try {
    // Compress the embedding vector before storage
    const compressedEmbedding = compressEmbedding(embedding);
    
    // Store compression ratio for analytics
    const originalSize = embedding.length * 4; // 4 bytes per float
    const compressedSize = compressedEmbedding.length;
    const compressionRatio = originalSize / compressedSize;
    
    // Track the size metrics
    updateCacheMetrics(EMBEDDING_STORE, 'set', { 
      compressionRatio,
      originalSize,
      compressedSize
    });
    
    const result = await setCachedItem(
      contentHash,
      { embedding: compressedEmbedding },
      EMBEDDING_STORE,
      { maxAge: CLIENT_CACHE_TTL.EMBEDDING }
    );
    
    return result;
  } catch (error) {
    console.error('Error compressing and storing embedding:', error);
    return false;
  }
};

// Enhanced client-side Response Cache Functions with partial invalidation support
export const getClientResponse = async (
  query: string,
  topic: TopicType,
  language: string = 'en'
): Promise<{ response: string, suggestions: string[] } | null> => {
  const queryHash = generateContentHash(query, { topic, language });
  const response = await getCachedItem<{ response: string, suggestions: string[], popularity: number }>(queryHash, RESPONSE_STORE);
  
  // Update access metrics
  if (response) {
    updateCacheMetrics(RESPONSE_STORE, 'hit', { topic, language });
    
    // If hit, increment popularity
    updateItemPopularity(queryHash, RESPONSE_STORE);
  } else {
    updateCacheMetrics(RESPONSE_STORE, 'miss', { topic, language });
  }
  
  return response ? { 
    response: response.response, 
    suggestions: response.suggestions || [] 
  } : null;
};

export const setClientResponse = async (
  query: string,
  topic: TopicType,
  language: string,
  response: string,
  suggestions: string[] = []
): Promise<boolean> => {
  const queryHash = generateContentHash(query, { topic, language });
  
  // Determine TTL based on content type
  let ttl = CLIENT_CACHE_TTL.RESPONSE;
  
  // Set initial popularity
  const initialPopularity = 1;
  
  const result = await setCachedItem(
    queryHash,
    { response, suggestions, popularity: initialPopularity, topic, language },
    RESPONSE_STORE,
    { maxAge: ttl }
  );
  
  // Update metrics if successful
  if (result) {
    updateCacheMetrics(RESPONSE_STORE, 'set', { topic, language });
  }
  
  return result;
};

// Client-side Messages Cache Functions with progressive loading
export const getClientMessages = async (
  sessionId: string,
  limit?: number
): Promise<MessageType[] | null> => {
  const messages = await getCachedItem<MessageType[]>(sessionId, MESSAGES_STORE);
  
  // Update access metrics
  if (messages) {
    updateCacheMetrics(MESSAGES_STORE, 'hit');
    
    // If limit is specified, return only the requested number of messages
    // This enables progressive loading
    if (limit && messages.length > limit) {
      return messages.slice(-limit); // Return the most recent messages
    }
  } else {
    updateCacheMetrics(MESSAGES_STORE, 'miss');
  }
  
  return messages;
};

export const setClientMessages = async (
  sessionId: string,
  messages: MessageType[]
): Promise<boolean> => {
  // Check if we need to limit the size of the messages
  const maxMessages = 100; // Maximum number of messages to store
  let messagesToStore = messages;
  
  if (messages.length > maxMessages) {
    // Keep only the most recent messages
    messagesToStore = messages.slice(-maxMessages);
    console.log(`Limiting cached messages to the ${maxMessages} most recent ones`);
  }
  
  const result = await setCachedItem(
    sessionId,
    messagesToStore,
    MESSAGES_STORE,
    { maxAge: CLIENT_CACHE_TTL.MESSAGES }
  );
  
  // Update metrics if successful
  if (result) {
    updateCacheMetrics(MESSAGES_STORE, 'set', { 
      messageCount: messagesToStore.length,
      totalSize: JSON.stringify(messagesToStore).length
    });
  }
  
  return result;
};

// Interface for cached items with popularity
interface CachedItemWithPopularity {
  value?: any;
  popularity?: number;
  [key: string]: any;
}

// Update item popularity (for adaptive TTL)
export const updateItemPopularity = async (
  key: string,
  storeName: string
): Promise<void> => {
  try {
    const item = await getCachedItem<CachedItemWithPopularity>(key, storeName);
    if (item && typeof item === 'object' && item !== null) {
      // Safely access/modify the popularity property
      const popularity = (item.popularity || 0) + 1;
      item.popularity = popularity;
      
      // Calculate adaptive TTL based on popularity
      let adaptiveTtl = CLIENT_CACHE_TTL.RESPONSE;
      
      if (popularity >= 5) {
        // Frequently used items get longer TTL
        adaptiveTtl = CLIENT_CACHE_TTL.FREQUENTLY_USED;
      } else if (popularity <= 1) {
        // Rarely used items get shorter TTL
        adaptiveTtl = CLIENT_CACHE_TTL.RARELY_USED;
      }
      
      // Update the item with new popularity and TTL
      await setCachedItem(key, item, storeName, { maxAge: adaptiveTtl });
    }
  } catch (error) {
    console.error('Error updating item popularity:', error);
  }
};

// Enhanced cache synchronization with partial updates
export const syncClientCache = async (
  sessionId: string,
  serverMessages: MessageType[]
): Promise<boolean> => {
  try {
    const localMessages = await getClientMessages(sessionId);
    
    // If we have local messages not synced to server, handle merging
    if (localMessages && localMessages.length > 0) {
      // Improved merging strategy - merge based on message IDs
      const mergedMessages = [...serverMessages];
      
      // Add any local messages that aren't in the server set
      for (const localMsg of localMessages) {
        if (!mergedMessages.some(msg => msg.id === localMsg.id)) {
          mergedMessages.push(localMsg);
        }
      }
      
      // Sort by timestamp
      mergedMessages.sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      return setClientMessages(sessionId, mergedMessages);
    }
    
    // Update the local cache with server messages
    return setClientMessages(sessionId, serverMessages);
  } catch (error) {
    console.error('Error syncing client cache:', error);
    return false;
  }
};

// Enhanced cache metrics tracking
interface CacheMetrics {
  hits: number;
  misses: number;
  sets: number;
  lastUpdated: Date;
  byLanguage?: Record<string, { hits: number, misses: number }>;
  byTopic?: Record<string, { hits: number, misses: number }>;
  sizeMetrics?: {
    totalSize: number;
    itemCount: number;
    avgCompressionRatio?: number;
  };
}

// Update cache metrics with enhanced tracking
export const updateCacheMetrics = async (
  storeName: string,
  action: 'hit' | 'miss' | 'set',
  metadata: Record<string, any> = {}
): Promise<void> => {
  try {
    // Get current metrics
    let metrics = await getCachedItem<CacheMetrics>(`metrics_${storeName}`, CACHE_STATS_STORE);
    
    // Initialize metrics if they don't exist
    if (!metrics) {
      metrics = {
        hits: 0,
        misses: 0,
        sets: 0,
        lastUpdated: new Date(),
        byLanguage: {},
        byTopic: {},
        sizeMetrics: {
          totalSize: 0,
          itemCount: 0
        }
      };
    }
    
    // Ensure sub-objects exist
    metrics.byLanguage = metrics.byLanguage || {};
    metrics.byTopic = metrics.byTopic || {};
    metrics.sizeMetrics = metrics.sizeMetrics || { totalSize: 0, itemCount: 0 };
    
    // Update language-specific metrics if language is provided
    if (metadata.language) {
      const language = metadata.language;
      metrics.byLanguage[language] = metrics.byLanguage[language] || { hits: 0, misses: 0 };
      
      if (action === 'hit') {
        metrics.byLanguage[language].hits = (metrics.byLanguage[language].hits || 0) + 1;
      } else if (action === 'miss') {
        metrics.byLanguage[language].misses = (metrics.byLanguage[language].misses || 0) + 1;
      }
    }
    
    // Update topic-specific metrics if topic is provided
    if (metadata.topic) {
      const topic = metadata.topic;
      metrics.byTopic[topic] = metrics.byTopic[topic] || { hits: 0, misses: 0 };
      
      if (action === 'hit') {
        metrics.byTopic[topic].hits = (metrics.byTopic[topic].hits || 0) + 1;
      } else if (action === 'miss') {
        metrics.byTopic[topic].misses = (metrics.byTopic[topic].misses || 0) + 1;
      }
    }
    
    // Update size metrics if available
    if (action === 'set') {
      // Update item count
      metrics.sizeMetrics.itemCount = (metrics.sizeMetrics.itemCount || 0) + 1;
      
      // Update total size if provided
      if (metadata.totalSize) {
        metrics.sizeMetrics.totalSize = (metrics.sizeMetrics.totalSize || 0) + metadata.totalSize;
      }
      
      // Update compression metrics for embeddings
      if (metadata.compressionRatio) {
        const currentRatio = metrics.sizeMetrics.avgCompressionRatio || 0;
        const currentCount = metrics.sets || 0;
        
        // Calculate new average compression ratio
        metrics.sizeMetrics.avgCompressionRatio = 
          (currentRatio * currentCount + metadata.compressionRatio) / (currentCount + 1);
      }
    }
    
    // Update the appropriate counter
    switch (action) {
      case 'hit':
        metrics.hits++;
        break;
      case 'miss':
        metrics.misses++;
        break;
      case 'set':
        metrics.sets++;
        break;
    }
    
    metrics.lastUpdated = new Date();
    
    // Save updated metrics
    await setCachedItem(
      `metrics_${storeName}`,
      metrics,
      CACHE_STATS_STORE,
      { maxAge: 30 * 24 * 60 * 60 * 1000 } // 30 days
    );
  } catch (error) {
    console.error('Error updating cache metrics:', error);
  }
};

// Get cache metrics for a store with enhanced information
export const getCacheMetrics = async (
  storeName: string
): Promise<CacheMetrics | null> => {
  try {
    return await getCachedItem<CacheMetrics>(`metrics_${storeName}`, CACHE_STATS_STORE);
  } catch (error) {
    console.error('Error getting cache metrics:', error);
    return null;
  }
};

// Get hit rate for a store
export const getCacheHitRate = async (
  storeName: string
): Promise<number | null> => {
  try {
    const metrics = await getCacheMetrics(storeName);
    if (!metrics) return null;
    
    const total = metrics.hits + metrics.misses;
    if (total === 0) return 0;
    
    return metrics.hits / total;
  } catch (error) {
    console.error('Error calculating hit rate:', error);
    return null;
  }
};

// Get metrics for all stores with enhanced detail
export const getAllCacheMetrics = async (): Promise<Record<string, CacheMetrics>> => {
  try {
    const results: Record<string, CacheMetrics> = {};
    
    for (const store of [EMBEDDING_STORE, RESPONSE_STORE, MESSAGES_STORE]) {
      const metrics = await getCacheMetrics(store);
      if (metrics) {
        results[store] = metrics;
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error getting all cache metrics:', error);
    return {};
  }
};

// Get cache size metrics for visualization
export const getCacheSizeMetrics = async (): Promise<Record<string, number>> => {
  try {
    const metrics = await getAllCacheMetrics();
    const sizeMetrics: Record<string, number> = {};
    
    for (const [store, storeMetrics] of Object.entries(metrics)) {
      sizeMetrics[store] = storeMetrics.sizeMetrics?.totalSize || 0;
    }
    
    return sizeMetrics;
  } catch (error) {
    console.error('Error getting cache size metrics:', error);
    return {};
  }
};

// Enhanced invalidation - clear cache by topic with relationship awareness
export const invalidateCacheByTopic = async (topic: string): Promise<number> => {
  try {
    // Get all response cache keys
    const keys = await getCacheKeys(RESPONSE_STORE);
    let invalidatedCount = 0;
    
    // Filter and delete keys related to the topic
    for (const key of keys) {
      try {
        const item = await getCachedItem<any>(key, RESPONSE_STORE);
        if (item && item.topic === topic) {
          await cleanupExpiredItems(RESPONSE_STORE, [key]);
          invalidatedCount++;
          
          // Check for related content
          const relatedContent = await getRelatedContentKeys(key, RESPONSE_STORE);
          if (relatedContent && relatedContent.relatedKeys.length > 0) {
            // Invalidate related content too
            await cleanupExpiredItems(relatedContent.relatedType, relatedContent.relatedKeys);
            invalidatedCount += relatedContent.relatedKeys.length;
          }
        }
      } catch (err) {
        console.error(`Error checking cache key ${key}:`, err);
      }
    }
    
    // Update metrics
    updateCacheMetrics(RESPONSE_STORE, 'set', { 
      invalidated: invalidatedCount,
      reason: 'topic',
      topic
    });
    
    // Publish event for subscribers
    cacheEventBus.publish('topicUpdated', { topic, invalidatedCount });
    
    return invalidatedCount;
  } catch (error) {
    console.error('Error invalidating cache by topic:', error);
    return 0;
  }
};

// Enhanced invalidation - clear cache by language with relationship awareness
export const invalidateCacheByLanguage = async (language: string): Promise<number> => {
  try {
    // Get all response cache keys
    const keys = await getCacheKeys(RESPONSE_STORE);
    let invalidatedCount = 0;
    
    // Filter and delete keys related to the language
    for (const key of keys) {
      try {
        const item = await getCachedItem<any>(key, RESPONSE_STORE);
        if (item && item.language === language) {
          await cleanupExpiredItems(RESPONSE_STORE, [key]);
          invalidatedCount++;
          
          // Check for related content
          const relatedContent = await getRelatedContentKeys(key, RESPONSE_STORE);
          if (relatedContent && relatedContent.relatedKeys.length > 0) {
            // Invalidate related content too
            await cleanupExpiredItems(relatedContent.relatedType, relatedContent.relatedKeys);
            invalidatedCount += relatedContent.relatedKeys.length;
          }
        }
      } catch (err) {
        console.error(`Error checking cache key ${key}:`, err);
      }
    }
    
    // Update metrics
    updateCacheMetrics(RESPONSE_STORE, 'set', { 
      invalidated: invalidatedCount,
      reason: 'language',
      language
    });
    
    // Publish event for subscribers
    cacheEventBus.publish('languageUpdated', { language, invalidatedCount });
    
    return invalidatedCount;
  } catch (error) {
    console.error('Error invalidating cache by language:', error);
    return 0;
  }
};

// New function: Invalidate cache based on knowledge base updates
export const invalidateCacheByKnowledgeUpdate = async (knowledgeId: string): Promise<number> => {
  try {
    // This would need integration with your knowledge base system
    // For now, we'll implement a basic version that invalidates responses
    // that might be affected by knowledge changes
    
    // Get all response cache keys
    const keys = await getCacheKeys(RESPONSE_STORE);
    let invalidatedCount = 0;
    
    // For a real implementation, you would need to track which responses
    // are derived from which knowledge base entries
    // For now, we'll use a simple approach to invalidate potentially affected responses
    
    // Publish event for subscribers
    cacheEventBus.publish('knowledgeUpdated', { knowledgeId, invalidatedCount });
    
    return invalidatedCount;
  } catch (error) {
    console.error('Error invalidating cache by knowledge update:', error);
    return 0;
  }
};

// Enhanced cleanup function with memory usage optimization and device capability awareness
export const cleanupClientCache = async (): Promise<void> => {
  try {
    // Standard cleanup of expired items
    await Promise.all([
      cleanupExpiredItems(EMBEDDING_STORE),
      cleanupExpiredItems(RESPONSE_STORE),
      cleanupExpiredItems(MESSAGES_STORE),
      cleanupExpiredItems(CACHE_STATS_STORE),
      cleanupExpiredItems(CACHE_RELATIONSHIPS_STORE)
    ]);
    
    // Enhanced device capability detection
    const deviceCapabilities = await detectDeviceCapabilities();
    
    // If device has limited memory, be more aggressive in cleanup
    if (deviceCapabilities.hasLimitedMemory) {
      console.log(`Low memory device detected (${deviceCapabilities.availableMemoryMB}MB), performing aggressive cache cleanup`);
      
      // Get current cache metrics to determine what to clean up
      const metrics = await getAllCacheMetrics();
      
      // Clean up low-value items with enhanced pruning logic
      for (const store of Object.keys(metrics)) {
        await pruneCache(store, deviceCapabilities);
      }
    }
  } catch (error) {
    console.error('Error cleaning up client cache:', error);
  }
};

// Enhanced device capability detection
export interface DeviceCapabilities {
  deviceMemoryGB: number;
  cpuCores: number;
  isMobile: boolean;
  browserName: string;
  hasLimitedMemory: boolean;
  availableMemoryMB: number;
  storageQuotaMB: number;
  supportsCompressionAPI: boolean;
  supportsBlobManager: boolean;
}

// Advanced detection of device capabilities
async function detectDeviceCapabilities(): Promise<DeviceCapabilities> {
  const capabilities: DeviceCapabilities = {
    deviceMemoryGB: 4, // Default assumption
    cpuCores: 4,       // Default assumption
    isMobile: false,
    browserName: 'unknown',
    hasLimitedMemory: false,
    availableMemoryMB: 1024, // Default assumption
    storageQuotaMB: 1024,    // Default assumption
    supportsCompressionAPI: false,
    supportsBlobManager: false
  };
  
  try {
    // Check for device memory API
    if ('navigator' in globalThis && 'deviceMemory' in navigator) {
      // @ts-ignore - deviceMemory is not in all TS definitions
      capabilities.deviceMemoryGB = (navigator as any).deviceMemory || 4;
    }
    
    // Check for hardware concurrency (CPU cores)
    if ('navigator' in globalThis && 'hardwareConcurrency' in navigator) {
      capabilities.cpuCores = navigator.hardwareConcurrency;
    }
    
    // Check if it's a mobile device
    if ('navigator' in globalThis && 'userAgent' in navigator) {
      const userAgent = navigator.userAgent.toLowerCase();
      capabilities.isMobile = /android|webos|iphone|ipad|ipod|blackberry|windows phone/.test(userAgent);
      
      // Detect browser
      if (userAgent.indexOf('chrome') > -1) capabilities.browserName = 'chrome';
      else if (userAgent.indexOf('safari') > -1) capabilities.browserName = 'safari';
      else if (userAgent.indexOf('firefox') > -1) capabilities.browserName = 'firefox';
      else if (userAgent.indexOf('edge') > -1) capabilities.browserName = 'edge';
    }
    
    // Check available memory
    if ('performance' in globalThis && 'memory' in performance) {
      // @ts-ignore - memory is not in all TS definitions
      const memory = (performance as any).memory;
      if (memory) {
        const totalHeapSize = memory.jsHeapSizeLimit;
        const usedHeapSize = memory.usedJSHeapSize;
        const availableHeap = totalHeapSize - usedHeapSize;
        capabilities.availableMemoryMB = Math.floor(availableHeap / (1024 * 1024));
      }
    }
    
    // Check storage quota
    if ('navigator' in globalThis && 'storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        if (estimate.quota) {
          capabilities.storageQuotaMB = Math.floor(estimate.quota / (1024 * 1024));
        }
      } catch (e) {
        console.warn('Error estimating storage quota:', e);
      }
    }
    
    // Check for Compression API support
    capabilities.supportsCompressionAPI = 'CompressionStream' in globalThis;
    
    // Check for experimental BlobManager API (not widely supported yet)
    // @ts-ignore - BlobManager is experimental
    capabilities.supportsBlobManager = 'BlobManager' in globalThis;
    
    // Determine if this is a limited memory device
    capabilities.hasLimitedMemory = capabilities.deviceMemoryGB < 4 || 
                                   capabilities.availableMemoryMB < 200 ||
                                   capabilities.isMobile;
    
    return capabilities;
  } catch (error) {
    console.error('Error detecting device capabilities:', error);
    return capabilities; // Return default capabilities
  }
}

// Enhanced pruning with more sophisticated algorithms
async function pruneCache(storeName: string, deviceCapabilities: DeviceCapabilities): Promise<number> {
  try {
    // Adaptive cache size limits based on device capabilities
    const maxItems = {
      [EMBEDDING_STORE]: deviceCapabilities.hasLimitedMemory ? 30 : 100,
      [RESPONSE_STORE]: deviceCapabilities.hasLimitedMemory ? 50 : 200,
      [MESSAGES_STORE]: deviceCapabilities.hasLimitedMemory ? 3 : 10,
    };
    
    const limit = maxItems[storeName as keyof typeof maxItems] || 100;
    
    // Get metrics to check item count
    const metrics = await getCacheMetrics(storeName);
    const itemCount = metrics?.sizeMetrics?.itemCount || 0;
    
    if (itemCount <= limit) {
      return 0; // No pruning needed
    }
    
    console.log(`Pruning ${storeName} cache: ${itemCount} items exceed limit of ${limit}`);
    
    // Get all keys and sort by relevance/popularity
    const keys = await getCacheKeys(storeName);
    let prunedCount = 0;
    
    // Get all items to assess their value
    const itemsToAssess: { key: string; value: any }[] = [];
    for (const key of keys) {
      const item = await getCachedItem(key, storeName);
      if (item) {
        itemsToAssess.push({ key, value: item });
      }
    }
    
    // Sort items by their value/popularity (if available)
    itemsToAssess.sort((a, b) => {
      const aPopularity = a.value.popularity || 0;
      const bPopularity = b.value.popularity || 0;
      return aPopularity - bPopularity; // Least popular first
    });
    
    // Keep only the most valuable items
    const itemsToRemove = itemsToAssess.slice(0, itemCount - limit);
    
    // Remove the least valuable items
    for (const item of itemsToRemove) {
      await cleanupExpiredItems(storeName, [item.key]);
      prunedCount++;
    }
    
    console.log(`Pruned ${prunedCount} items from ${storeName} cache`);
    return prunedCount;
  } catch (error) {
    console.error(`Error pruning cache for ${storeName}:`, error);
    return 0;
  }
}

// Enhanced optimization with recommendations and content relationships
export const optimizeClientCache = async (): Promise<void> => {
  try {
    console.log('Cache optimization started');
    
    // Get cache metrics
    const metrics = await getAllCacheMetrics();
    
    // Enhanced cleanup with device awareness
    const deviceCapabilities = await detectDeviceCapabilities();
    if (deviceCapabilities.hasLimitedMemory) {
      console.log('Low memory device detected, performing targeted optimization');
    }
    
    // Run cleanup
    await cleanupClientCache();
    
    // Analyze cache effectiveness with enhanced insights
    const recommendations: string[] = [];
    
    // Analyze hit rates with more detail
    for (const [store, storeMetrics] of Object.entries(metrics)) {
      const hitRate = storeMetrics.hits / (storeMetrics.hits + storeMetrics.misses || 1);
      
      if (hitRate < 0.3) {
        recommendations.push(`Low hit rate (${(hitRate * 100).toFixed(1)}%) for ${store} cache. Consider adjusting TTL or caching strategy.`);
      }
      
      // More detailed language analysis
      if (storeMetrics.byLanguage) {
        for (const [lang, langMetrics] of Object.entries(storeMetrics.byLanguage)) {
          const langHitRate = langMetrics.hits / (langMetrics.hits + langMetrics.misses || 1);
          if (langHitRate < 0.2) {
            recommendations.push(`Very low hit rate (${(langHitRate * 100).toFixed(1)}%) for ${lang} in ${store} cache.`);
          }
        }
      }
      
      // Topic-specific recommendations
      if (storeMetrics.byTopic) {
        for (const [topic, topicMetrics] of Object.entries(storeMetrics.byTopic)) {
          const topicHitRate = topicMetrics.hits / (topicMetrics.hits + topicMetrics.misses || 1);
          if (topicHitRate < 0.2) {
            recommendations.push(`Low hit rate for topic "${topic}". Consider pre-caching common queries.`);
          }
        }
      }
    }
    
    // Add device-specific recommendations
    if (deviceCapabilities.hasLimitedMemory) {
      recommendations.push(`Limited memory device detected (${deviceCapabilities.availableMemoryMB}MB available). Consider reducing max cache size.`);
    }
    
    if (deviceCapabilities.isMobile && !deviceCapabilities.supportsCompressionAPI) {
      recommendations.push('Mobile device without compression API detected. Consider using smaller embedding models for better performance.');
    }
    
    // Store recommendations for the admin UI
    await setCachedItem(
      'cache_optimization_recommendations',
      { recommendations, timestamp: new Date() },
      CACHE_STATS_STORE,
      { maxAge: 24 * 60 * 60 * 1000 } // 24 hours
    );
    
    console.log('Cache optimization completed with recommendations:', recommendations);
  } catch (error) {
    console.error('Error optimizing client cache:', error);
  }
};

// Get cache optimization recommendations
export const getCacheOptimizationRecommendations = async (): Promise<string[]> => {
  try {
    const data = await getCachedItem<{ recommendations: string[] }>(
      'cache_optimization_recommendations', 
      CACHE_STATS_STORE
    );
    return data?.recommendations || [];
  } catch (error) {
    console.error('Error getting cache recommendations:', error);
    return [];
  }
};

// Cache initialization with enhanced setup
export const initializeClientCache = (): void => {
  // Clean up expired items on startup
  cleanupClientCache().catch(console.error);
  
  // Set up periodic cleanup (every hour)
  setInterval(() => {
    cleanupClientCache().catch(console.error);
  }, 60 * 60 * 1000);
  
  // Set up periodic cache optimization (every 6 hours)
  setInterval(() => {
    optimizeClientCache().catch(console.error);
  }, 6 * 60 * 60 * 1000);
  
  // Set up listeners for cache invalidation events
  setupCacheEventListeners();
};

// Setup cache event listeners
function setupCacheEventListeners(): void {
  // Listen for knowledge base updates
  cacheEventBus.subscribe('knowledgeUpdated', (payload) => {
    console.log(`Knowledge base updated (ID: ${payload.knowledgeId}), invalidating related cache entries`);
    // Additional logic if needed
  });
  
  // Listen for topic updates
  cacheEventBus.subscribe('topicUpdated', (payload) => {
    console.log(`Topic updated (${payload.topic}), ${payload.invalidatedCount} cache entries invalidated`);
  });
}
